/**
 * Review dialog for multi-source connection import.
 *
 * Lists the scanned candidates grouped under their source group headers, each
 * with a checkbox (default checked except duplicates), auth badges, an optional
 * inline password (prefilled from documented plaintext files), and a Browse…
 * fallback. Import saves the checked connections; nothing auto-connects. Title
 * and copy adapt to the active source.
 *
 * Opened from the New Connection dialog it sits ON TOP of it (Option C, C3):
 * after a successful import the dialog under it — the one registered when the
 * review opened, if it is still there (`useOriginatingHost`) — is told: with no draft input it closes itself; with a
 * draft it stays and says what was saved, and no toast is shown (its "View
 * connections" would unmount the draft).
 */
import { useEffect, useMemo, useState } from 'react';
import { FolderSearch } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { ScrollArea } from '../../../components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { toast } from '../../../components/ui/toaster';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { useConnectionStore } from '../store/connectionStore';
import { useConnectionsUIStore } from '../store/uiStore';
import { liveHost, useOriginatingHost } from '../store/connectionDialogHostStore';
import { useConnectionImportStore } from './connectionImportStore';
import { ImportCandidateRow } from './ImportCandidateRow';
import { importSelectedCandidates, runScanFile } from './connectionImportService';
import { importSourceLabel, isFilelessSource } from './connectionImportSources';
import { groupCandidatesByPath, isDuplicateCandidate } from './connectionImportUtils';

type RowState = { checked: boolean; password: string };

export function ImportConnectionsDialog() {
  const isOpen = useConnectionImportStore((s) => s.isOpen);
  const scanResult = useConnectionImportStore((s) => s.scanResult);
  const sourceId = useConnectionImportStore((s) => s.sourceId);
  const entry = useConnectionImportStore((s) => s.entry);
  const close = useConnectionImportStore((s) => s.close);
  const setScanResult = useConnectionImportStore((s) => s.setScanResult);
  const connections = useConnectionStore((s) => s.connections);
  const setShowConnectionsDialog = useConnectionsUIStore((s) => s.setShowConnectionsDialog);
  const { track } = useAnalytics();
  // The New Connection dialog this review opened on top of (never a newer one — C3-05).
  const originHost = useOriginatingHost(isOpen);

  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [importing, setImporting] = useState(false);
  const [browsing, setBrowsing] = useState(false);

  const label = importSourceLabel(sourceId);
  const candidates = useMemo(() => scanResult?.candidates ?? [], [scanResult]);
  const duplicateKeys = useMemo(() => {
    const set = new Set<string>();
    for (const candidate of candidates) {
      if (isDuplicateCandidate(candidate, connections)) set.add(candidate.key);
    }
    return set;
  }, [candidates, connections]);

  // (Re)initialise selection whenever a new scan result arrives. Supported,
  // non-duplicate rows are checked by default; duplicates start unchecked.
  // Passwords found in documented plaintext files prefill the field.
  useEffect(() => {
    const next: Record<string, RowState> = {};
    for (const candidate of candidates) {
      next[candidate.key] = {
        checked: candidate.supported && !duplicateKeys.has(candidate.key),
        password: candidate.prefilledPassword ?? '',
      };
    }
    setRows(next);
  }, [candidates, duplicateKeys]);

  const checkedCount = useMemo(
    () => candidates.filter((c) => c.supported && rows[c.key]?.checked).length,
    [candidates, rows]
  );

  const grouped = useMemo(() => groupCandidatesByPath(candidates), [candidates]);
  const hasCandidates = candidates.length > 0;

  const handleBrowse = async () => {
    setBrowsing(true);
    try {
      const result = await runScanFile(sourceId);
      if (result.cancelled) return;
      setScanResult(result, 'file_picker');
    } finally {
      setBrowsing(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    // This save belongs to the review and the New Connection dialog it started in (C3-12): both are captured now, so a
    // review or dialog opened while it is held is never closed or told when it settles.
    const operation = { host: originHost(), generation: useConnectionImportStore.getState().generation };
    try {
      const selections = candidates
        .filter((c) => c.supported && rows[c.key]?.checked)
        .map((c) => ({ candidate: c, password: rows[c.key]?.password }));
      const imported = await importSelectedCandidates(selections, sourceId, entry);
      // Close first, then hand off: the toast's action opens Saved
      // Connections one tick later so the Radix dialog teardown never
      // overlaps the next dialog mounting (Docker-discovery handoff
      // pattern). Viewing only — nothing auto-connects.
      if (useConnectionImportStore.getState().generation === operation.generation) close();
      const host = liveHost(operation.host);
      if (imported > 0 && host?.completed({ via: sourceId === 'azure' ? 'azure_review' : 'import', count: imported, sourceLabel: label }) === 'kept') return;
      toast.success({
        title: `Imported ${imported} connection${imported === 1 ? '' : 's'}`,
        description: 'Find them in Saved Connections — connect when you are ready.',
        action: {
          label: 'View connections',
          onClick: () => {
            track('feature:connection_import_view_saved', { count: imported });
            setTimeout(() => setShowConnectionsDialog(true), 0);
          },
        },
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent
        className="sm:max-w-[620px] max-h-[85vh] flex flex-col"
        data-testid="import-connections-dialog"
        // On top of the New Connection dialog (C3): focus goes back to the entry that opened this flow.
        onCloseAutoFocus={(event) => {
          const dialogHost = originHost();
          if (!dialogHost) return;
          event.preventDefault();
          dialogHost.restoreFocus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Import from {label}</DialogTitle>
        </DialogHeader>

        <p className="-mt-1 truncate text-xs text-muted-foreground" title={scanResult?.filePath} data-testid="import-source-line">
          {scanResult?.filePath
            ? `${scanResult.filePath} · ${candidates.length} connection${candidates.length === 1 ? '' : 's'}`
            : sourceId === 'azure'
              // Azure has no file: the line names the picks (it used to say nothing was found, above the list of picks).
              ? `${candidates.length} ${candidates.length === 1 ? 'resource' : 'resources'} picked from your Azure account`
              : `No ${label} connections were found on this computer.`}
        </p>

        {hasCandidates ? (
          <ScrollArea className="flex-1 min-h-0 -mx-1 pr-2">
            {grouped.map((group) => (
              <div key={group.path ?? '__ungrouped__'} className="mb-1">
                {group.path && (
                  <div className="px-2 pt-2 pb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {group.path}
                  </div>
                )}
                {group.candidates.map((candidate) => (
                  <ImportCandidateRow
                    key={candidate.key}
                    candidate={candidate}
                    checked={rows[candidate.key]?.checked ?? false}
                    duplicate={duplicateKeys.has(candidate.key)}
                    password={rows[candidate.key]?.password ?? ''}
                    onToggle={(checked) =>
                      setRows((prev) => ({
                        ...prev,
                        [candidate.key]: { ...prev[candidate.key], checked },
                      }))
                    }
                    onPasswordChange={(password) =>
                      setRows((prev) => ({
                        ...prev,
                        [candidate.key]: { ...prev[candidate.key], password },
                      }))
                    }
                  />
                ))}
              </div>
            ))}
          </ScrollArea>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 py-8 text-center">
            <FolderSearch className="h-8 w-8 opacity-40 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              {scanResult?.error
                ? `That file could not be read as ${label} connections.`
                : `Nothing to import yet — point us at a ${label} connections file.`}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2 border-t pt-3">
          <p className="text-xs text-muted-foreground">
            Passwords are never read from another app&apos;s secure store — add them now or on first
            connect.
          </p>
          <div className="flex items-center justify-between">
            {/* Fileless sources (Azure) have no on-disk file to browse — a
                Browse… here would run parseFile() → [] and wipe the picked
                candidates. Hidden for them; other sources keep the fallback. */}
            {isFilelessSource(sourceId) ? (
              <span />
            ) : (
              <Button
                variant="link"
                size="sm"
                className="h-auto px-0 text-xs"
                onClick={handleBrowse}
                disabled={browsing}
                data-testid="import-browse-button"
              >
                Can&apos;t find your file? Browse…
              </Button>
            )}
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={close} data-testid="import-cancel-button">
                Cancel
              </Button>
              {checkedCount === 0 ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>
                      <Button disabled data-testid="import-submit-button">
                        Import 0 connections
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Select at least one connection to import.</TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  data-testid="import-submit-button"
                >
                  {`Import ${checkedCount} connection${checkedCount === 1 ? '' : 's'}`}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
